import {
    convertMillisecondsToLocalTimeZone,
    convertToMilliSeconds,
    getEpochFromDateString,
} from '@thoughtspot/date-format-util';
import { translate } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { isThisPeriodInDateFiltersEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import {
    DateFilterType,
    DateFilterValue,
    DateFilterValueInput,
    DatePeriod,
    FalconDataType,
    Filter,
    FilterColumn,
    FilterContentInput,
    FilterDateRangeInput,
    FilterGroup,
    FilterTypes,
} from '/@services/generated/graphql-types';
import { getDateStringFromEpoch } from '/@utils/date-time.util';
import { logger } from '../../token-bar/tokenizer/tokenizer.util';
import { FILTER_TYPE, getFilterType } from '../answer-filter.util';
import { CollectEventFunction } from '../answer-filter-metrics.util';
import { NUMERICAL_DATE_BUCKETS } from '../date-range-filter/date-range-filter.util';
import { DateFilterProto, DatePickerRange, DateValues } from './date-filter';
import {
    DateFilterTabTypes,
    getTabFromDateFilterProto,
} from './date-filter-tabs/date-filter-tabs.util';

export enum DateFilterErrorType {
    sameDateRange = 'SAME_DATE_RANGE',
    noStartDate = 'NO_START_DATE',
    noEndDate = 'NO_END_DATE',
    noYear = 'NO_YEAR',
    invalidYear = 'INVALID_YEAR',
    wrongDaterange = 'WRONG_DATE_RANGE',
}

export const MIN_YEAR = 1000;

const MIXPANEL_PROPERTY_FIXED_SOURCE = 'fixed-date-filter';
const MIXPANEL_PROPERTY_ROLLING_SOURCE = 'rolling-date-filter';
const MIXPANEL_PROPERTY_DEFAULT_VALUE_SET = 'Set';
const MIXPANEL_PROPERTY_DEFAULT_VALUE_NOT_SET = 'Not Set';

export const getErrorTypeToErrorMessage = (): {
    [key in DateFilterErrorType]: string;
} => {
    return {
        [DateFilterErrorType.sameDateRange]: translate(
            'dateFilter.errors.sameDateRange',
        ),
        [DateFilterErrorType.wrongDaterange]: translate(
            'dateFilter.errors.wrongDateRange',
        ),
        [DateFilterErrorType.noStartDate]: translate(
            'dateFilter.emptyStartDate.error',
        ),
        [DateFilterErrorType.noEndDate]: translate(
            'dateFilter.emptyEndDate.error',
        ),
        [DateFilterErrorType.noYear]: translate(
            'dateFilter.emptyYearvalue.error',
        ),
        [DateFilterErrorType.invalidYear]: translate(
            'dateFilter.invalidYearvalue.error',
        ),
    };
};

/**
 * @param ms - milliseconds
 */
export function convertToSeconds(ms: number): number | null {
    if (_.isNil(ms)) {
        return null;
    }
    const factor = 1000;
    let result;
    if (typeof ms === 'string') {
        result = parseInt(ms, 10) / factor;
    } else {
        result = ms / factor;
    }

    return Math.floor(result);
}

/**
 * getEpochFromDate uses GetEpochFromDateString which always return a epoch value according
 * to the cluster time zone not according to the browser for example if cluster in US then
 * it returns a epoch value according to the US time zone
 */
export function getEpochFromDate(date: Date): number | null {
    if (_.isNil(date)) {
        return null;
    }
    const epochMs = getEpochFromDateString(
        date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        }),
        'MM/dd/yyyy', // US date format
    );
    if (!epochMs) {
        logger.warn('unexpected error please check the value');
    }
    return convertToSeconds(epochMs);
}

export function getDateFromEpoch(epoch: number | string): Date {
    if (_.isNil(epoch)) {
        return null;
    }
    const localTime = convertMillisecondsToLocalTimeZone(
        convertToMilliSeconds(epoch),
    );
    return new Date(localTime);
}

export const getDatePickerDigestableValues = (
    dateFilterValue: DateFilterValue,
): { op: FilterTypes; type: DateFilterType; dateValue: DateValues } => {
    if (_.isNil(dateFilterValue)) {
        return {
            op: null,
            type: null,
            dateValue: null,
        };
    }
    const {
        op: filterType,
        type: dateFilterType,
        ...dateValue
    } = dateFilterValue;
    const { epoch, dateRange, ...valuesWithoutEpoch } = dateValue || {};
    const completeDateValues = {
        epoch: getDateFromEpoch(epoch),
        dateRange: {
            lowEpoch: getDateFromEpoch(dateRange?.lowEpoch),
            highEpoch: getDateFromEpoch(dateRange?.highEpoch),
        },
        ...valuesWithoutEpoch,
    };
    return {
        op: filterType,
        type: dateFilterType,
        dateValue: completeDateValues,
    };
};

/**
 * If there is more than one date filter applied then it can only be edited
 * using the search bar. Thus we only return the date values from the
 * first element in the filters array
 * We will support multiple filters once we have Custom Date filter tab
 * @param filters
 */
export const getDateFilterObject = (filters: Filter[]): DateFilterProto => {
    let completeDateValues: DateValues = null;
    let dateFilterType: DateFilterType = null;
    let filterType: FilterTypes = null;
    let isNegate = false;
    let includeCurrentPeriod: boolean | undefined;

    if (!_.isEmpty(filters)) {
        // Considering the first element because if there are
        // more than one date filters applied on a column, they can
        // only be edited using search bar.
        const { dateFilterContent: dateFilterContents } = _.head(filters);
        // only considering one dateFilterContent
        const dateFilterContent = _.head(dateFilterContents);
        const obj = getDatePickerDigestableValues(
            dateFilterContent?.dateFilter,
        );
        filterType = obj?.op;
        dateFilterType = obj?.type;
        completeDateValues = obj?.dateValue;
        isNegate = dateFilterContent?.negate;
        // Extract includeCurrentPeriod from the API response
        includeCurrentPeriod =
            dateFilterContent?.dateFilter?.includeCurrentPeriod;
    }
    return {
        dateValue: completeDateValues,
        dateFilterType,
        filterType,
        isNegate,
        includeCurrentPeriod,
    };
};

/**
 * If there is more than one date filter applied then it can only be edited
 * using the search bar
 * @param filters
 */
export const getAppliedFilterIds = (filters: Filter[]): string[] => {
    return filters?.map(({ filterId }) => filterId) || [];
};

export const isDateFilterDuplicate = (
    updatedContent: DateFilterValueInput,
    appliedContent: DateFilterValue,
) => {
    const {
        op: updatedOp,
        type: updatedType1,
        epoch: updatedEpoch,
        dateRange: updatedDateRange,
        ...updatedDateValue
    } = updatedContent || {};
    const {
        op,
        type,
        epoch: appliedEpoch,
        dateRange: appliedDateRange,
        ...appliedDateValue
    } = appliedContent;

    if (
        !_.isNil(updatedDateValue) &&
        _.isEqual(updatedEpoch?.toString(), appliedEpoch) &&
        _.isEqual(
            updatedDateRange?.lowEpoch?.toString(),
            appliedDateRange?.lowEpoch,
        ) &&
        _.isEqual(
            updatedDateRange?.highEpoch?.toString(),
            appliedDateRange?.highEpoch,
        ) &&
        _.isEqual(updatedDateValue, appliedDateValue)
    ) {
        return true;
    }
    return false;
};

export const isEmptyFilter = (
    dateFilterType: DateFilterType,
    dateRange: FilterDateRangeInput | DatePickerRange,
    epoch: Date | number,
): boolean => {
    const lowEpoch = dateRange?.lowEpoch;
    const highEpoch = dateRange?.highEpoch;
    return (
        (_.isEqual(dateFilterType, DateFilterType.ExactDateRange) &&
            _.isNil(lowEpoch) &&
            _.isNil(highEpoch)) ||
        (_.isEqual(dateFilterType, DateFilterType.ExactDate) && _.isNil(epoch))
    );
};

export const getSanitisedFilterContentForFixed = (
    selectedDateFilter: DateFilterProto,
): {
    filterContent?: FilterContentInput;
    isInvalid?: boolean;
    errorType?: DateFilterErrorType;
} => {
    const { dateFilterType, filterType, dateValue, isNegate } =
        selectedDateFilter || {};
    let finalDateFilterContent: DateFilterValueInput = {};
    const lowEpoch = dateValue?.dateRange?.lowEpoch;
    const highEpoch = dateValue?.dateRange?.highEpoch;
    // check for empty filter scenario
    if (isEmptyFilter(dateFilterType, dateValue?.dateRange, dateValue?.epoch)) {
        return {
            filterContent: {
                dateFilterContent: {
                    type: DateFilterType.ExactDate,
                    op: FilterTypes.Eq,
                },
                negate: false,
            },
            isInvalid: false,
        };
    }
    if (_.isEqual(dateFilterType, DateFilterType.ExactDateRange)) {
        if (
            !_.isNil(dateValue?.dateRange) &&
            !_.isNil(lowEpoch) &&
            !_.isNil(highEpoch)
        ) {
            if (_.isEqual(lowEpoch, highEpoch)) {
                return {
                    isInvalid: true,
                    errorType: DateFilterErrorType.sameDateRange,
                };
            }
            if (lowEpoch > highEpoch) {
                return {
                    isInvalid: true,
                    errorType: DateFilterErrorType.wrongDaterange,
                };
            }
            finalDateFilterContent = {
                dateRange: {
                    lowEpoch: getEpochFromDate(lowEpoch),
                    highEpoch: getEpochFromDate(highEpoch),
                },
            };
        } else {
            if (_.isNil(lowEpoch)) {
                return {
                    isInvalid: true,
                    errorType: DateFilterErrorType.noStartDate,
                };
            }
            return {
                isInvalid: true,
                errorType: DateFilterErrorType.noEndDate,
            };
        }
    }
    if (_.isEqual(dateFilterType, DateFilterType.ExactDate)) {
        finalDateFilterContent = { epoch: getEpochFromDate(dateValue?.epoch) };
    }
    if (_.isEqual(dateFilterType, DateFilterType.MonthYear)) {
        if (_.isNil(dateValue?.yearName) || _.isEmpty(dateValue?.yearName))
            return {
                isInvalid: true,
                errorType: DateFilterErrorType.noYear,
            };

        if (+dateValue?.yearName < MIN_YEAR)
            return {
                isInvalid: true,
                errorType: DateFilterErrorType.invalidYear,
            };
        finalDateFilterContent = {
            monthName: dateValue?.monthName,
            yearName: dateValue?.yearName,
        };
    }
    if (_.isEqual(dateFilterType, DateFilterType.QuarterYear)) {
        if (_.isNil(dateValue?.yearName) || _.isEmpty(dateValue?.yearName))
            return {
                isInvalid: true,
                errorType: DateFilterErrorType.noYear,
            };

        if (+dateValue?.yearName < MIN_YEAR)
            return {
                isInvalid: true,
                errorType: DateFilterErrorType.invalidYear,
            };
        finalDateFilterContent = {
            quarterName: dateValue?.quarterName,
            yearName: dateValue?.yearName,
        };
    }
    if (_.isEqual(dateFilterType, DateFilterType.YearOnly)) {
        if (_.isNil(dateValue?.yearName) || _.isEmpty(dateValue?.yearName)) {
            return {
                isInvalid: true,
                errorType: DateFilterErrorType.noYear,
            };
        }

        if (+dateValue?.yearName < MIN_YEAR) {
            return {
                isInvalid: true,
                errorType: DateFilterErrorType.invalidYear,
            };
        }
        finalDateFilterContent = { yearName: dateValue?.yearName };
    }
    finalDateFilterContent.type = dateFilterType;
    finalDateFilterContent.op = filterType;
    return {
        filterContent: {
            dateFilterContent: finalDateFilterContent,
            negate: isNegate,
        },
        isInvalid: false,
    };
};

export const getSanitisedFilterContentForRolling = (
    selectedDateFilter: DateFilterProto,
): {
    filterContent?: FilterContentInput;
    isInvalid?: boolean;
    errorType?: DateFilterErrorType;
} => {
    const {
        dateFilterType,
        filterType,
        dateValue,
        isNegate,
        includeCurrentPeriod,
    } = selectedDateFilter || {};

    let finalDateFilterContent: DateFilterValueInput = {};

    if (
        [DateFilterType.ThisPeriod, DateFilterType.PeriodToDate].includes(
            dateFilterType,
        )
    ) {
        finalDateFilterContent = {
            datePeriod: dateValue?.datePeriod,
        };
    }

    if (
        _.includes(
            [DateFilterType.NextNPeriod, DateFilterType.LastNPeriod],
            dateFilterType,
        )
    ) {
        finalDateFilterContent = {
            datePeriod: dateValue?.datePeriod,
            number: dateValue?.number,
            // Only include current period when feature flag is enabled
            ...(isThisPeriodInDateFiltersEnabled() && {
                includeCurrentPeriod: includeCurrentPeriod ?? false,
            }),
        };
    }

    finalDateFilterContent.type = dateFilterType || DateFilterType.Yesterday;
    finalDateFilterContent.op = filterType || FilterTypes.Eq;
    return {
        filterContent: {
            dateFilterContent: finalDateFilterContent,
            negate: isNegate,
        },
        isInvalid: false,
    };
};

export const getSanitisedFilterContent = (
    selectedDateFilter: DateFilterProto,
    tab: DateFilterTabTypes,
) => {
    return _.isEqual(tab, DateFilterTabTypes.Fixed)
        ? getSanitisedFilterContentForFixed(selectedDateFilter)
        : getSanitisedFilterContentForRolling(selectedDateFilter);
};

// duplicate filter
// invalid state
// we need to sanitize the dateFilterContent object (we are not passing any property which is not required)
export const getApplicableFilterDetails = (
    filters: Filter[],
    selectedDateFilter: DateFilterProto,
): {
    filterContent?: FilterContentInput[];
    removeFilterIds?: string[];
    isInvalid?: boolean;
    isDuplicate?: boolean;
    errorType?: DateFilterErrorType;
} => {
    const { dateFilterType: finalDateFilterType } = selectedDateFilter || {};
    const filterIds = getAppliedFilterIds(filters);
    const {
        dateFilterType: initDateFilterType,
        filterType: initFilterType,
        dateValue: initDateValue,
        isNegate: initNegate,
    } = getDateFilterObject(filters);

    const tab = getTabFromDateFilterProto(finalDateFilterType);

    // get Sanitize object and check isInvalid filter
    const {
        filterContent: finalFilterContent,
        isInvalid,
        errorType,
    } = getSanitisedFilterContent(selectedDateFilter, tab);

    if (isInvalid) {
        return { isInvalid, errorType };
    }

    const { dateFilterContent: finalDateFilterContent, negate: finalIsNegate } =
        finalFilterContent || {};

    // check for duplicate filter
    if (
        _.isEqual(finalDateFilterType, initDateFilterType) &&
        _.isEqual(finalDateFilterContent?.op, initFilterType) &&
        _.isEqual(finalIsNegate, initNegate)
    ) {
        const { dateFilterContent: initdateFilterContents } = _.head(filters);
        // only considering one dateFilterContent
        const initDateFilterContent = _.head(initdateFilterContents);
        if (
            isDateFilterDuplicate(
                finalDateFilterContent,
                initDateFilterContent?.dateFilter,
            )
        )
            return { isDuplicate: true };
    }

    return {
        filterContent: [{ ...finalFilterContent, filterId: _.head(filterIds) }],
        isDuplicate: false,
        isInvalid: false,
        removeFilterIds: [],
    };
};

export const getDateString = (date: Date): string => {
    return getDateStringFromEpoch(getEpochFromDate(date));
};
export const ROLLING_POSSIBLE_UNIT_VALUES = [
    DatePeriod.Day,
    DatePeriod.Week,
    DatePeriod.Month,
    DatePeriod.Quarter,
    DatePeriod.Second,
    DatePeriod.Minute,
    DatePeriod.Hour,
    DatePeriod.Year,
];

export const ROLLING_DATE_FILTER_TYPE_WiTH_PERIODS = [
    DateFilterType.ThisPeriod,
    DateFilterType.LastNPeriod,
    DateFilterType.NextNPeriod,
    DateFilterType.PeriodToDate,
];

export const ROLLING_DATE_FILTER_TYPE_POSSIBLE_VALUES = [
    DateFilterType.Yesterday,
    DateFilterType.Today,
    DateFilterType.Tomorrow,
    ...ROLLING_DATE_FILTER_TYPE_WiTH_PERIODS,
];

export const DATE_FILTER_TYPE_POSSIBLE_VALUES = [
    DateFilterType.ExactDate,
    DateFilterType.ExactDateRange,
    DateFilterType.MonthYear,
    DateFilterType.QuarterYear,
    DateFilterType.YearOnly,
    ...ROLLING_DATE_FILTER_TYPE_POSSIBLE_VALUES,
];

export const DATE_POSSIBLE_OPERATORS_VALUES = (
    dateFilterType: DateFilterType,
) => {
    switch (dateFilterType) {
        case DateFilterType.ExactDate:
            return [
                FilterTypes.Eq,
                FilterTypes.Ne,
                FilterTypes.Le,
                FilterTypes.Lt,
                FilterTypes.Gt,
                FilterTypes.Ge,
            ];
        case DateFilterType.ExactDateRange:
            return [FilterTypes.BwInc];
        case DateFilterType.ThisPeriod:
        case DateFilterType.LastNPeriod:
        case DateFilterType.NextNPeriod:
        case DateFilterType.Yesterday:
        case DateFilterType.Today:
        case DateFilterType.Tomorrow:
            return [
                FilterTypes.Eq,
                FilterTypes.Le,
                FilterTypes.Lt,
                FilterTypes.Gt,
                FilterTypes.Ge,
            ];
        default:
            return [FilterTypes.Eq];
    }
};

/**
 * Return false if filter can only be edited using search bar, true otherwise
 * @param filters
 * @param filterColumn
 */
export function canEditFilterUsingWidgetDateModal(
    filters: Filter[],
    filterColumn: FilterColumn,
) {
    const { dataType, timeBucket } = filterColumn;
    const dateFilterDataType = [FalconDataType.Date, FalconDataType.DateTime];
    if (!dateFilterDataType.includes(dataType)) {
        return false;
    }
    if (!_.isEmpty(filters)) {
        const { dateFilterContent } = _.head(filters);
        if (
            _.size(filters) > 1 ||
            _.size(dateFilterContent) > 1 ||
            NUMERICAL_DATE_BUCKETS.includes(timeBucket) ||
            _.isNil(dateFilterContent) ||
            _.isEmpty(dateFilterContent)
        ) {
            return false;
        }
        const dateFilterContentObj = _.head(dateFilterContent);
        const dateFilter = dateFilterContentObj?.dateFilter;

        // If includeCurrentPeriod feature is disabled but filter has it enabled,
        // restrict editing via widget
        if (
            !isThisPeriodInDateFiltersEnabled() &&
            dateFilter?.includeCurrentPeriod
        ) {
            return false;
        }

        if (!_.includes(DATE_FILTER_TYPE_POSSIBLE_VALUES, dateFilter?.type)) {
            return false;
        }

        if (
            !_.includes(
                DATE_POSSIBLE_OPERATORS_VALUES(dateFilter?.type),
                dateFilter?.op,
            )
        ) {
            return false;
        }

        if (
            _.includes(
                ROLLING_DATE_FILTER_TYPE_WiTH_PERIODS,
                dateFilter?.type,
            ) &&
            !_.includes(ROLLING_POSSIBLE_UNIT_VALUES, dateFilter?.datePeriod)
        ) {
            return false;
        }
    }
    return true;
}

export const collecDateFilterMixpanelEvent = (
    selectedFilter: DateFilterProto,
    collectEvent: CollectEventFunction,
) => {
    const { dateFilterType, dateValue, includeCurrentPeriod } =
        selectedFilter || {};
    const isEmpty = isEmptyFilter(
        dateFilterType,
        dateValue?.dateRange,
        dateValue?.epoch,
    );
    const tab = getTabFromDateFilterProto(dateFilterType);

    // Modify type to include _THIS_PERIOD suffix when includeCurrentPeriod is enabled
    let type: string = dateFilterType;
    if (isThisPeriodInDateFiltersEnabled() && includeCurrentPeriod) {
        type = `${dateFilterType}_${DateFilterType.ThisPeriod}`;
    }

    collectEvent(new UserAction(userActionTypeEnum.APPLY_DATE_FILTER), {
        source:
            tab === DateFilterTabTypes.Fixed
                ? MIXPANEL_PROPERTY_FIXED_SOURCE
                : MIXPANEL_PROPERTY_ROLLING_SOURCE,
        type,
        defaultValue: isEmpty
            ? MIXPANEL_PROPERTY_DEFAULT_VALUE_NOT_SET
            : MIXPANEL_PROPERTY_DEFAULT_VALUE_SET,
    });
};

const isFixedDateFilter = (filterGroup: FilterGroup) => {
    return (
        getTabFromDateFilterProto(
            filterGroup.filters[0].dateFilterContent[0].dateFilter.type,
        ) === DateFilterTabTypes.Fixed
    );
};
const shouldNotMatchPhrase = (filterGroup: FilterGroup) => {
    // if date filter content is not present early return
    if (_.isEmpty(filterGroup?.filters?.[0]?.dateFilterContent)) {
        return true;
    }
    // if filter group has more than one filter or more than one date filter content then it should not match the phrase
    return (
        _.size(filterGroup?.filters) > 1 ||
        _.size(filterGroup?.filters?.[0]?.dateFilterContent) > 1 ||
        isFixedDateFilter(filterGroup)
    );
};

export const getMatchingPhrase = (
    filterGroup: FilterGroup,
    sageSearchPhraseMap: Record<string, string[]>,
) => {
    // to check if phrase map is non empty and filter is date range filter and filter is editable using widget date modal
    if (!_.isEmpty(sageSearchPhraseMap) && !shouldNotMatchPhrase(filterGroup)) {
        // get formula/column id which will be id for phrase map
        const colId = filterGroup?.groupId?.answerColumnId;
        return _.head(sageSearchPhraseMap[colId]);
    }
    return undefined;
};

export const getDateFilterCategory = (type: DateFilterType) => {
    const tab = getTabFromDateFilterProto(type);
    return tab === DateFilterTabTypes.Fixed
        ? MIXPANEL_PROPERTY_FIXED_SOURCE
        : MIXPANEL_PROPERTY_ROLLING_SOURCE;
};
